import { rem, type MantineTheme } from "@mantine/core";
import type { MantineThemeOverride } from "@mantine/core";
import { IconFileUpload } from "../lib/hugeiconsTablerCompat";
import {
  DROPDOWN_RADIUS,
  FIELD_RADIUS,
  SEGMENTED_CONTROL_RADIUS_PX,
  TAG_PILL_RADIUS,
  dashboardTagLightStyle,
} from "./dashboardTagTokens";

/** Cards, inputs, badges, nav — first half of dashboard component theme */
export const mantineComponentsSurfaces: MantineThemeOverride["components"] = {
  Card: {
    defaultProps: {
      padding: "md",
      shadow: "none",
      radius: "lg",
      withBorder: true,
    },
    styles: {
      root: {
        transition: "box-shadow 150ms ease",
        "&:hover": {
          boxShadow: "var(--mantine-shadow-sm)",
        },
      },
    },
  },
  Paper: {
    defaultProps: {
      shadow: "none",
    },
  },
  Flex: {
    defaultProps: {
      gap: "md",
    },
  },
  Badge: {
    defaultProps: {
      variant: "light",
      size: "lg",
    },
    styles: (theme: MantineTheme, props: { color?: unknown; variant?: string }) => ({
      root: {
        borderRadius: TAG_PILL_RADIUS,
        fontWeight: 600,
        paddingInline: rem(16),
        paddingBlock: rem(7),
        minHeight: rem(34),
        ...dashboardTagLightStyle(theme, props.color, props.variant),
      },
      section: {
        color: "inherit",
        display: "flex",
        alignItems: "center",
      },
      label: {
        fontSize: rem(14),
        lineHeight: 1.35,
        textTransform: "none",
        color: "inherit",
      },
    }),
  },
  Button: {
    defaultProps: {
      radius: FIELD_RADIUS,
      size: "md",
    },
    styles: {
      root: { fontWeight: 500 },
      label: { fontSize: rem(14) },
    },
  },
  ActionIcon: {
    defaultProps: {
      radius: FIELD_RADIUS,
      size: "md",
    },
  },
  CloseButton: {
    defaultProps: {
      radius: FIELD_RADIUS,
      size: "md",
    },
  },
  SegmentedControl: {
    defaultProps: {
      radius: SEGMENTED_CONTROL_RADIUS_PX,
      size: "md",
    },
  },
  Pagination: {
    defaultProps: {
      size: "md",
    },
  },
  Avatar: {
    defaultProps: {
      radius: "xl",
      size: "lg",
    },
  },
  Fieldset: {
    defaultProps: {
      radius: "lg",
      variant: "filled",
    },
  },
  Blockquote: {
    defaultProps: {
      radius: "lg",
    },
  },
  Alert: {
    defaultProps: {
      radius: "lg",
      variant: "light",
    },
  },
  TextInput: {
    defaultProps: {
      size: "md",
      radius: FIELD_RADIUS,
    },
  },
  PasswordInput: {
    defaultProps: {
      size: "md",
      radius: FIELD_RADIUS,
    },
  },
  NumberInput: {
    defaultProps: {
      size: "md",
      radius: FIELD_RADIUS,
    },
  },
  Textarea: {
    defaultProps: {
      radius: FIELD_RADIUS,
    },
  },
  FileInput: {
    defaultProps: {
      size: "md",
      radius: FIELD_RADIUS,
      leftSection: <IconFileUpload size={18} />,
      leftSectionPointerEvents: "none",
    },
    styles: {
      input: {
        backgroundColor: "#F2F2F4",
        border: "1px dashed #c2c6cf",
      },
    },
  },
    ColorInput: {
      defaultProps: {
        radius: FIELD_RADIUS,
      },
      styles: {
        dropdown: {
          borderRadius: DROPDOWN_RADIUS,
          border: "1px solid #E3E3E5",
          padding: rem(4),
        },
      },
    },
    Pill: {
      defaultProps: {
        size: "lg",
      },
      styles: {
        root: {
          borderRadius: TAG_PILL_RADIUS,
          fontWeight: 600,
        },
        label: {
          fontSize: rem(14),
          textTransform: "none",
        },
      },
    },

    ScrollArea: {
      defaultProps: {
        radius: FIELD_RADIUS,
      },
    },
};
